import { CHANNELS } from "../shared/channels";
import { Suggestion } from "../shared/achievements";
import { call, listen } from "./bridge";
import { byId, flashToast } from "./dom";
import { t } from "./i18n";
import { amOwner } from "./me";
import { onViewShown } from "./navigation";
import { Kanban, KanbanCard, KanbanColumn, buildArmedTrash, createKanban, openColumnPicker } from "./kanban";

// suggestions for improving chix, with votes, laid out as a board. everyone with the feature can post and vote, fractal moves the cards

let items: Suggestion[] = [];
// the owner's open note editor
let editing: { id: string; note: string } | null = null;
let board: Kanban<Suggestion> | null = null;

const COLUMN_COLORS: Record<Suggestion["status"], string> = { open: "var(--ink-3)", planned: "var(--amber)", done: "var(--green)", declined: "var(--red)" };

function columns(): KanbanColumn[] {
  return (["open", "planned", "done", "declined"] as const).map((id) => ({ id, label: t(id), color: COLUMN_COLORS[id] }));
}

function buildNoteEditor(item: Suggestion): HTMLElement {
  const draft = editing!;
  const row = document.createElement("div");
  row.className = "suggest-edit-row";
  const note = document.createElement("input");
  note.className = "suggest-note-input";
  note.maxLength = 200;
  note.value = draft.note;
  note.placeholder = t("note (optional)");
  note.setAttribute("aria-label", t("note (optional)"));
  note.addEventListener("input", () => {
    draft.note = note.value;
  });
  const save = document.createElement("button");
  save.type = "button";
  save.className = "pill accent suggest-save";
  save.textContent = t("save");
  save.addEventListener("click", () => {
    editing = null;
    void run(CHANNELS.suggestionsStatus, item.id, item.status, draft.note.trim());
  });
  row.append(note, save);
  return row;
}

function buildTile(card: KanbanCard<Suggestion>): HTMLElement {
  const item = card.data;
  const tile = document.createElement("li");
  tile.className = `task-tile suggest-tile status-${item.status}`;
  const vote = document.createElement("button");
  vote.type = "button";
  vote.className = `suggest-vote${item.voted ? " on" : ""}`;
  vote.setAttribute("aria-pressed", String(item.voted));
  vote.setAttribute("aria-label", t("vote"));
  vote.innerHTML = `<span aria-hidden="true">▲</span><b></b>`;
  vote.querySelector("b")!.textContent = String(item.votes);
  vote.addEventListener("click", () => void run(CHANNELS.suggestionsVote, item.id));
  const body = document.createElement("div");
  body.className = "suggest-body";
  const text = document.createElement("span");
  text.className = "card-title";
  text.textContent = item.text;
  const meta = document.createElement("small");
  meta.textContent = item.name;
  body.append(text, meta);
  if (item.note) {
    const note = document.createElement("em");
    note.className = "suggest-note";
    note.textContent = item.note;
    body.append(note);
  }
  const top = document.createElement("div");
  top.className = "suggest-tile-top";
  top.append(vote, body);
  tile.append(top);
  const foot = document.createElement("div");
  foot.className = "task-tile-foot";
  if (amOwner()) {
    const move = document.createElement("button");
    move.type = "button";
    move.className = "pill task-move";
    move.textContent = `${t(item.status)} ▾`;
    move.addEventListener("click", () => openColumnPicker(item.text, columns(), item.status, (next) => void run(CHANNELS.suggestionsStatus, item.id, next, item.note)));
    const pen = document.createElement("button");
    pen.type = "button";
    pen.className = "pill icon-only";
    pen.setAttribute("aria-label", t("note (optional)"));
    pen.textContent = "✎";
    pen.addEventListener("click", () => {
      editing = editing?.id === item.id ? null : { id: item.id, note: item.note };
      paint();
    });
    foot.append(move, pen);
  }
  if (item.mine || amOwner()) {
    foot.append(buildArmedTrash(t("remove"), true, () => void run(CHANNELS.suggestionsRemove, item.id)));
  }
  if (foot.children.length) tile.append(foot);
  if (editing?.id === item.id && amOwner()) tile.append(buildNoteEditor(item));
  return tile;
}

function paint(): void {
  board ??= createKanban<Suggestion>({
    host: byId("suggest-board"),
    columns: columns(),
    build: (card) => buildTile(card),
    move: (card, to) => void run(CHANNELS.suggestionsStatus, card.key, to, card.data.note),
    trash: (card) => void run(CHANNELS.suggestionsRemove, card.key),
    trashMode: (card) => (card.data.mine || amOwner() ? "arm" : "none"),
    canMove: () => amOwner(),
    emptyText: t("nothing here")
  });
  board.paint(items.map((item) => ({ key: item.id, column: item.status, data: item })));
  byId("suggest-empty").hidden = items.length > 0;
}

async function run(channel: string, ...args: unknown[]): Promise<void> {
  try {
    items = await call<Suggestion[]>(channel, ...args);
    paint();
  } catch (error) {
    flashToast(String(error instanceof Error ? error.message : error).replace(/^.*Error: /, ""));
  }
}

async function load(): Promise<void> {
  try {
    items = await call<Suggestion[]>(CHANNELS.suggestionsList);
    paint();
  } catch {
    // keep what is showing
  }
}

export function setupSuggestModule(): void {
  const form = byId<HTMLFormElement>("suggest-form");
  form.addEventListener("submit", (event) => {
    event.preventDefault();
    const input = byId<HTMLInputElement>("suggest-input");
    if (!input.value.trim()) return;
    void run(CHANNELS.suggestionsPost, input.value).then(() => {
      input.value = "";
    });
  });
  listen(CHANNELS.suggestionsUpdated, () => void load());
  onViewShown("today", () => void load());
  void load();
}
